/**
 * Stable idempotency keys for Studio commands the server replays once committed.
 *
 * A key is found again only for the same signed-in account (the ID token's `sub`), the same book,
 * the same family command scope, the same source unit and the exact same payload. So:
 *  - refreshing and sending the identical command reuses the key (a lost answer is replayed);
 *  - changed words, a different revision or a different chapter/recipe/page mints a new key;
 *  - a different account never sees, reuses or keeps another account's entries: they are dropped
 *    the first time the new account sends anything, and on sign-out.
 *
 * Journal entries hold digests and random keys only: never source words, tokens or the subject
 * itself (the subject is digested too).
 *
 * Session storage is only a convenience. When it is unavailable (blocked or full), the journal
 * lives in this page's memory instead: a retry from the same open page still reuses its key, a
 * reload mints a fresh one, and the server's expected-revision fence keeps that later attempt from
 * applying twice.
 */
const journalPrefix = "lifescapsule:studio-command-retry:";
const journalVersion = "v2:";
const inFlight = new Map<string, Promise<unknown>>();
/** Page-memory fallback when session storage is unavailable; keyed exactly like storage. */
const memoryJournal = new Map<string, string>();

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value !== null && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => [key, canonical(item)]),
    );
  return value;
}

export const studioCommandDigest = (value: unknown) => sha256(value);

async function sha256(value: unknown) {
  const bytes = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(JSON.stringify(canonical(value))),
  );
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** The signed-in account a token belongs to: its `sub`, or the whole token when it has none. */
export function studioCommandSubject(idToken: string) {
  try {
    const payload = idToken.split(".")[1] ?? "";
    const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
    const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as {
      sub?: unknown;
    };
    return typeof claims.sub === "string" && claims.sub ? claims.sub : idToken;
  } catch {
    return idToken;
  }
}

function journalKeys(storage: Storage) {
  return Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
    (key): key is string => Boolean(key?.startsWith(journalPrefix)),
  );
}

/** Forgets every retry identity, for every account (sign-out). Storage failures are ignored. */
export function clearStudioCommandJournal() {
  memoryJournal.clear();
  try {
    for (const key of journalKeys(sessionStorage)) sessionStorage.removeItem(key);
  } catch {
    // Nothing persisted when storage is unavailable; the memory journal is already cleared.
  }
}

/** Drops entries that are not this account's: another subject's, or older unscoped ones. */
function forgetOtherSubjects(subjectPrefix: string) {
  for (const key of memoryJournal.keys())
    if (!key.startsWith(subjectPrefix)) memoryJournal.delete(key);
  try {
    for (const key of journalKeys(sessionStorage))
      if (!key.startsWith(subjectPrefix)) sessionStorage.removeItem(key);
  } catch {
    // Unreadable storage holds nothing this account could reuse.
  }
}

function rememberedKey(journalKey: string) {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(journalKey);
  } catch {
    // Unavailable storage: the page-memory journal below is the only one.
  }
  const key = stored ?? memoryJournal.get(journalKey) ?? crypto.randomUUID();
  try {
    // Persist before dispatch, so a lost ACK survives refresh without another mutation identity.
    sessionStorage.setItem(journalKey, key);
    memoryJournal.delete(journalKey);
  } catch {
    memoryJournal.set(journalKey, key);
  }
  return key;
}

function forget(journalKey: string) {
  memoryJournal.delete(journalKey);
  try {
    sessionStorage.removeItem(journalKey);
  } catch {
    // Already only in memory.
  }
}

export type ReplayableStudioCommandInput<TWire, TResult> = {
  /** The signed-in account's ID token; only its digested `sub` scopes the journal. */
  idToken: string;
  bookId: string;
  /** The family command, e.g. `chapter:save`, `recipe:restore`, `variant:SAVE_SCRIPT`. */
  scope: string;
  /** The chapter, recipe, page or spread the command is about; the book itself when omitted. */
  sourceUnit?: string;
  command: unknown;
  write: (idempotencyKey: string) => Promise<TWire>;
  validate: (result: TWire) => TResult;
};

/** Retry identity only: no source words, auth token or browser-owned production state is stored. */
export async function replayableStudioCommand<TWire, TResult = TWire>(
  input: ReplayableStudioCommandInput<TWire, TResult>,
): Promise<TResult> {
  const subjectPrefix = `${journalPrefix}${journalVersion}${(
    await sha256({ subject: studioCommandSubject(input.idToken) })
  ).slice(0, 32)}:`;
  forgetOtherSubjects(subjectPrefix);
  const digest = await sha256({
    bookId: input.bookId,
    scope: input.scope,
    sourceUnit: input.sourceUnit ?? null,
    command: input.command,
  });
  const journalKey = `${subjectPrefix}${digest}`;
  const pending = inFlight.get(journalKey);
  if (pending) return pending as Promise<TResult>;
  const key = rememberedKey(journalKey);
  const operation = Promise.resolve()
    .then(() => input.write(key))
    .then((result) => {
      const verified = input.validate(result);
      forget(journalKey);
      return verified;
    })
    .finally(() => inFlight.delete(journalKey));
  inFlight.set(journalKey, operation);
  return operation;
}
